/**
 * The pages that exist before anybody has an account.
 *
 * Four of them: an affiliate opening an invitation, an affiliate confirming an
 * address, an operator signing itself up, and an operator proving its domain.
 * All four are reached by someone with no session, usually from a link in an
 * email opened days later on a different device.
 *
 * They were query flags on the console's own address — `/?signup`. Two problems
 * with that, one cosmetic and one not. The cosmetic one: `?confirm=01J…` reads
 * like a tracking parameter, and people strip those. The real one: a query flag
 * is invisible to everything that reasons about a site. It cannot be linked
 * from a marketing page, cannot be checked by the tool that compares the mail's
 * links against the portal's routes, and gives the person who mistypes it a
 * console asking them to sign in rather than the page they wanted.
 *
 * So: real paths, matched on the last segment so the same code works at the
 * site root (`/signup`) and under a path prefix (`/preview/signup`).
 *
 * These must stay identical to `PORTAL_PATHS` in api-core, which is what the
 * outbound mail links to. `check-boundaries` compares the two, because mail and
 * portal drifting apart is invisible in every test on either side and shows up
 * only as a customer clicking a link that 404s.
 */

export type PublicPage = 'invite' | 'verify' | 'signup' | 'confirm' | 'apply';

const PAGES: readonly PublicPage[] = ['invite', 'verify', 'signup', 'confirm', 'apply'];

export interface PublicRoute {
  readonly page: PublicPage | null;
  /** The token from `?token=`, when the page needs one. */
  readonly token: string | null;
  /**
   * A trailing path segment, for the one page that needs a name in the URL.
   *
   * `/apply/northline` — an operator prints that link, and there is no session
   * and nothing else on the page to say which programme it is. A token would
   * be wrong here: this link is public, permanent and reused by everybody.
   */
  readonly tail: string | null;
}

export function publicRoute(location: { pathname: string; search: string }): PublicRoute {
  const segments = location.pathname.split('/').filter(Boolean).map((s) => s.replace(/\.html$/, ''));
  const last = segments[segments.length - 1] ?? '';
  const previous = segments[segments.length - 2] ?? '';
  const token = new URLSearchParams(location.search).get('token');

  if (PAGES.includes(last as PublicPage)) return { page: last as PublicPage, token, tail: null };
  if (PAGES.includes(previous as PublicPage)) {
    return { page: previous as PublicPage, token, tail: last };
  }
  return { page: null, token, tail: null };
}

/** Public pages that carry their own one-time credential in `?token=`. */
const TOKEN_QUERY_PAGES = new Set<PublicPage>(['invite', 'verify', 'confirm']);

/**
 * A tab-session token from `?token=` on the AffDice Admin, when that query param
 * is not already owned by a pre-auth page such as `/confirm`.
 *
 * "Login as" opens a new tab with `?token=…&brand_id=…`. Domain confirmation
 * mail links to `/confirm?token=…`. Both use the same parameter name; only one
 * meaning applies per URL.
 */
export function readConsoleSessionToken(
  location: { pathname: string; search: string },
): string | null {
  const { page } = publicRoute(location);
  if (page !== null && TOKEN_QUERY_PAGES.has(page)) return null;
  return new URLSearchParams(location.search).get('token');
}
